import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { subscribe, type BillingPlan } from '../../lib/api/billing'
import { fetchOfferings, isNativeIAPPlatform, restorePurchases } from '../../lib/api/iap'
import { useAuth } from '../../lib/auth'
import { refreshSubscriptionStatus } from '../../lib/useSubscription'
import { readError } from '../../lib/errors'
import { useT } from '../../i18n'

// Apple's standard EULA — every app gets this by default unless we
// provide our own. Linking to Apple's hosted version is the path of
// least maintenance and is exactly what App Review wants to see for
// the "Terms of Use" link on a subscription paywall.
const APPLE_EULA_URL = 'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/'
// MUST be an absolute URL, not a relative '/privacy' path. In the native
// iOS app the WebView is served from a local bundle with no SPA fallback,
// so a relative '/privacy' link resolves to nothing and the reviewer sees a
// dead link — that's the App Review 3.1.2(c) rejection ("missing a
// functional link to the privacy policy"). The absolute URL opens the live
// policy in the browser on both native and web, exactly like the Terms
// link above does with Apple's EULA URL.
const PRIVACY_URL = 'https://games.jordandraper.com/privacy'

interface Props {
  onDismiss: () => void
}

interface PlanDisplay {
  plan: BillingPlan
  /** Headline price as it should appear in the UI. */
  priceString: string
  /** Per-period sublabel (e.g. "per year", "per month"). */
  perLabel: string
  /** Optional "savings" line shown under the annual plan. */
  saveLabel?: string
}

const FEATURES: { image: string; text: string }[] = [
  { image: '/games/ideatthat.webp',         text: "All six app games: I'D EAT THAT, COLORFUL, THREE STEP, FAKE OUT, CLOSE ENOUGH, PORTABLE PARTY GAMES" },
  { image: '/games/colorful.webp',          text: 'Host rooms, quick match, pass and play, and more' },
  { image: '/games/tokyo-metro.webp',       text: 'AI opponents with unique personalities' },
  { image: '/trophies/colorful-classic.webp', text: 'Weekly community events + voting' },
  { image: '/trophies/iet-classic.webp',    text: 'Trophies, awards & profile badges' },
  { image: '/games/three-step.webp',        text: 'Every new app game, unlocked the day it launches' },
]

/**
 * Web fallback prices — match the Stripe products and the App Store
 * subscription tiers. We hardcode these on web because we don't run
 * Stripe Price API calls just to render the paywall; the actual
 * authoritative price the user pays is whatever the next page (Stripe
 * Checkout or StoreKit sheet) shows them.
 */
const WEB_FALLBACK: Record<BillingPlan, PlanDisplay> = {
  yearly:  { plan: 'yearly',  priceString: '$24.99', perLabel: 'per year',  saveLabel: 'Save 58%' },
  monthly: { plan: 'monthly', priceString: '$4.99',  perLabel: 'per month' },
}

function billedThroughLine(t: (k: string) => string): string {
  if (!Capacitor.isNativePlatform()) return t('Billed securely through Stripe.')
  const p = Capacitor.getPlatform()
  if (p === 'ios')     return t('Billed through Apple. Manage from iOS Settings.')
  if (p === 'android') return t('Billed through Google Play. Manage from the Play Store.')
  return t('Billed securely.')
}

export function PaywallScreen({ onDismiss }: Props) {
  const t = useT()
  const { session, isGuest, signOut } = useAuth()
  const [plans, setPlans]       = useState<Record<BillingPlan, PlanDisplay>>(WEB_FALLBACK)
  const [selected, setSelected] = useState<BillingPlan>('yearly')
  const [busy, setBusy]         = useState(false)
  const [restoring, setRestoring] = useState(false)
  const [error, setError]       = useState<string | null>(null)
  const [info, setInfo]         = useState<string | null>(null)
  const isNative = isNativeIAPPlatform()
  // Guests (anonymous sessions) have no email, so the store webhook can
  // never attach their payment to an account — they'd be charged real
  // money for features the backend keeps refusing. Require a real
  // account before any purchase can start.
  const needsAccount = isGuest || !session?.user?.email

  async function handleRestore() {
    if (restoring) return
    setRestoring(true); setError(null); setInfo(null)
    try {
      await restorePurchases()
      // Tell every mounted useSubscription() to refetch so the rest of
      // the app unlocks immediately instead of after a restart.
      refreshSubscriptionStatus()
      setInfo(t('Purchases restored. If you had an active subscription it should unlock now.'))
    } catch (e) {
      setError(readError(e))
    } finally {
      setRestoring(false)
    }
  }

  // On native, swap in the real RevenueCat-reported prices so the
  // paywall reflects whatever the current store (sandbox vs prod, US
  // vs other localized prices) is actually about to charge. On web
  // we keep the hardcoded fallback — Stripe Checkout shows the real
  // price on the next screen anyway.
  useEffect(() => {
    if (!isNativeIAPPlatform()) return
    let cancelled = false
    fetchOfferings().then(pkgs => {
      if (cancelled || pkgs.length === 0) return
      const next = { ...WEB_FALLBACK }
      for (const pkg of pkgs) {
        if (pkg.plan === 'yearly') {
          next.yearly = {
            plan: 'yearly',
            priceString: pkg.priceString,
            perLabel: 'per year',
            saveLabel: t('Best value'),
          }
        }
        if (pkg.plan === 'monthly') {
          next.monthly = {
            plan: 'monthly',
            priceString: pkg.priceString,
            perLabel: 'per month',
          }
        }
      }
      setPlans(next)
    }).catch(() => { /* keep web fallback */ })
    return () => { cancelled = true }
  }, [])

  async function handleSubscribe() {
    if (busy) return
    // Defensive double-gate — the CTA is swapped out for guests, but
    // never let an anonymous session reach StoreKit/Stripe.
    if (needsAccount) {
      setError(t('Create a free account first so your subscription has somewhere to live.'))
      return
    }
    setBusy(true); setError(null)
    try {
      await subscribe(selected)
      // On web, subscribe() navigates to Stripe and never returns.
      // On native, the StoreKit sheet has resolved by here — refresh
      // subscription state everywhere, then bounce back to wherever
      // opened the paywall so the unlocked UI takes over.
      refreshSubscriptionStatus()
      onDismiss()
    } catch (e) {
      setError(readError(e))
      setBusy(false)
    }
  }

  // Guest path: drop the anonymous session and land on the auth screen
  // where they can create the account the subscription will attach to.
  async function handleCreateAccount() {
    try { await signOut() } catch { /* auth screen shows regardless */ }
  }

  return (
    <div className="screen bg-brand-text text-white">
      <div className="flex justify-end px-5 pt-5">
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('Close')}
          className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center
                     active:bg-white/20 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="flex-1 px-6 pb-8 flex flex-col">
        <div className="flex flex-col items-center text-center pt-2 pb-6">
          <img
            src="/jdg-logo.webp"
            alt="JDG"
            className="w-16 h-16 mb-5 drop-shadow-[0_0_20px_rgba(200,219,74,0.4)]"
          />
          <h1 className="font-display text-3xl font-black mb-2 text-brand-gold">{t('All-Access Pass')}</h1>
          <p className="text-white/60 text-balance max-w-xs">
            {t('Every game in the app, every weekly, AI opponents. One subscription.')}
          </p>
        </div>

        <div className="space-y-3 mb-6">
          {FEATURES.map(f => (
            <div key={f.text} className="flex items-center gap-3">
              <img
                src={f.image}
                alt=""
                className="w-9 h-9 rounded-full object-cover bg-white/10 shrink-0"
              />
              <span className="text-sm font-medium">{t(f.text)}</span>
            </div>
          ))}
        </div>

        {/* Plan picker */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <PlanTile
            display={plans.yearly}
            isSelected={selected === 'yearly'}
            onSelect={() => setSelected('yearly')}
          />
          <PlanTile
            display={plans.monthly}
            isSelected={selected === 'monthly'}
            onSelect={() => setSelected('monthly')}
          />
        </div>

        {error && (
          <p className="text-sm text-rose-300 mb-3 text-center">{error}</p>
        )}
        {info && (
          <p className="text-sm text-emerald-300 mb-3 text-center">{info}</p>
        )}

        <div className="space-y-2 mt-auto">
          {needsAccount ? (
            <>
              <button
                type="button"
                onClick={handleCreateAccount}
                className="w-full py-4 rounded-2xl bg-brand-primary text-white
                           font-semibold text-base
                           active:scale-95 transition-transform"
              >
                {t('Create a free account to subscribe')}
              </button>
              <p className="text-center text-white/50 text-xs leading-relaxed">
                {t("Subscriptions attach to your account's email so they restore on any device. Create the free account first, then start your 3-day free trial.")}
              </p>
            </>
          ) : (
          <button
            type="button"
            onClick={handleSubscribe}
            disabled={busy}
            className="w-full py-4 rounded-2xl bg-brand-primary text-white
                       font-semibold text-base
                       active:scale-95 transition-transform
                       disabled:opacity-60"
          >
            {busy ? t('Opening…') : t('Start 3-day free trial')}
          </button>
          )}

          {/* Auto-renewal disclosure — Apple requires this exact-ish copy
              on any paywall for an auto-renewing subscription. Length,
              price-per-period, what unlocks, and cancellation terms must
              all be stated in plain English on the same screen as the
              subscribe button. (Hidden for guests — they see the
              create-account CTA instead of a subscribe button.) */}
          {!needsAccount && (
          <p className="text-center text-white/50 text-xs leading-relaxed">
            {t('Then {price} {per}.', { price: plans[selected].priceString, per: t(plans[selected].perLabel) })}{' '}
            {t('Subscription auto-renews unless cancelled at least 24 hours before the end of the current period. Manage or cancel any time in your account settings.')}{' '}
            {t(billedThroughLine(t))}
          </p>
          )}

          {/* Restore Purchases — Apple-required button on iOS for any
              app that sells subscriptions. Web users don't need it
              (Stripe Customer Portal handles it) but it's harmless to
              show; we hide it on web to keep the page clean. */}
          {isNative && (
            <button
              type="button"
              onClick={handleRestore}
              disabled={restoring}
              className="w-full py-2 text-center text-white/60 text-xs underline
                         active:text-white disabled:opacity-50"
            >
              {restoring ? t('Restoring…') : t('Restore purchases')}
            </button>
          )}

          <p className="text-center text-white/40 text-[11px] pt-1">
            <a
              href={PRIVACY_URL}
              target="_blank" rel="noopener noreferrer"
              className="underline active:text-white"
            >
              {t('Privacy Policy')}
            </a>
            <span className="mx-2">·</span>
            <a
              href={APPLE_EULA_URL}
              target="_blank" rel="noopener noreferrer"
              className="underline active:text-white"
            >
              {t('Terms of Use')}
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

function PlanTile({
  display, isSelected, onSelect,
}: {
  display: PlanDisplay
  isSelected: boolean
  onSelect: () => void
}) {
  const t = useT()
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative rounded-2xl border-2 p-4 text-left transition-all
        ${isSelected
          ? 'border-brand-gold bg-brand-gold/10'
          : 'border-white/15 bg-white/5 active:bg-white/10'}`}
    >
      {display.saveLabel && (
        <span
          className={`absolute -top-2 right-3 text-[10px] font-bold uppercase tracking-wide
            px-2 py-0.5 rounded-full
            ${isSelected ? 'bg-brand-gold text-brand-text' : 'bg-white/20 text-white'}`}
        >
          {t(display.saveLabel)}
        </span>
      )}
      <div className="font-display text-2xl font-black">{display.priceString}</div>
      <div className="text-xs text-white/60 mt-1">{t(display.perLabel)}</div>
    </button>
  )
}
